import React from 'react';
import { Box, Typography, TextField, Button, Chip, Stack, Divider, Paper, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl, IconButton, Collapse, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export default function SQLValidation() {
  const { t } = useTranslation();
  const [env, setEnv] = React.useState<'production'|'staging'|'beta'|'production-new'>('production');
  const [ids, setIds] = React.useState<string>('');
  const [runId, setRunId] = React.useState<string>('');
  const [result, setResult] = React.useState<any>(null);
  const [filterText, setFilterText] = React.useState<string>('');
  const [filterStatus, setFilterStatus] = React.useState<'all'|'pending'|'running'|'passed'|'failed'|'error'>('all');
  const [onlyFailed, setOnlyFailed] = React.useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = React.useState<boolean>(true);
  const [sseReady, setSseReady] = React.useState<boolean>(false);
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const start = async () => {
    const list = Array.from(new Set(ids.split(/\s+/).filter(Boolean)));
    if (list.length === 0) return;
    
    // 🎯 关键修复：清空旧的runId，强制创建新验证
    setRunId('');
    setExpandedRows(new Set());
    
    // 🎯 立即显示pending状态，避免空白
    setResult({
      id: 'pending',
      env,
      status: 'pending',
      startedAt: new Date().toISOString(),
      items: list.map(orderId => ({
        orderId,
        status: 'pending',
        summary: t('sqlValidation.waitingForValidation')
      }))
    });
    
    try {
      const res = await fetch(`${API_ENDPOINTS.API_BASE}/api/validation/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ env, orderIds: list, options: { concurrency: 8 } })
      });
      const data = await res.json();
      const newRunId = data.validationRunId;
      setRunId(newRunId);
      
      // 启动后立即监听 SSE
      tryStartSSE(newRunId);
      
      // 🎯 立即刷新一次以获取初始状态
      setTimeout(async () => {
        try {
          const statusRes = await fetch(`${API_ENDPOINTS.API_BASE}/api/validation/run/${newRunId}`);
          const statusData = await statusRes.json();
          setResult(statusData);
        } catch (err) {
          console.error('Failed to fetch initial status:', err);
        }
      }, 200);
    } catch (error) {
      console.error('Failed to start validation:', error);
      setResult({
        id: 'error',
        env,
        status: 'failed',
        startedAt: new Date().toISOString(),
        items: list.map(orderId => ({
          orderId,
          status: 'error',
          summary: t('sqlValidation.failedToStart'),
          error: String(error)
        }))
      });
    }
  };

  const refresh = async () => {
    if (!runId) return;
    const res = await fetch(`${API_ENDPOINTS.API_BASE}/api/validation/run/${runId}`);
    const data = await res.json();
    setResult(data);
  };

  const download = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `validation_${runId}.json`; a.click(); URL.revokeObjectURL(url);
  };

  const downloadCsv = () => {
    if (!result) return;
    const rows = (result.items || []).map((i: any) => [i.orderId, i.status, i.summary || '', i.durationMs || '', (i.details || '').replace(/\n/g, ' ') ]);
    const header = ['orderId','status','summary','durationMs','details'];
  const csv = [header, ...rows].map((r: any[]) => r.map((v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `validation_${runId}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const tryStartSSE = (id: string) => {
    try {
      const es = new EventSource(`${API_ENDPOINTS.API_BASE}/api/validation/run/${id}/stream`);
      setSseReady(true);
      es.onmessage = (ev) => {
        try { setResult(JSON.parse(ev.data)); } catch {}
      };
      es.addEventListener('end', () => { es.close(); setSseReady(false); });
      es.onerror = () => { es.close(); setSseReady(false); };
    } catch {
      setSseReady(false);
    }
  };

  React.useEffect(() => {
    if (runId && autoRefresh && !sseReady) {
      const t = setInterval(() => { refresh().catch(()=>{}); }, 1200);
      return () => clearInterval(t);
    }
  }, [runId, autoRefresh, sseReady]);

  // 过滤后的表格数据
  const items = React.useMemo(() => {
    const arr = Array.isArray(result?.items) ? result.items : [];
    return arr.filter((i: any) => {
      if (onlyFailed && !(i.status === 'failed' || i.status === 'error')) return false;
      if (filterStatus !== 'all' && i.status !== filterStatus) return false;
      if (filterText && !String(i.orderId).includes(filterText)) return false;
      return true;
    });
  }, [result, onlyFailed, filterStatus, filterText]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('sqlValidation.title')}</Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Chip label={t('sqlValidation.environments.production')} color={env==='production'?'primary':'default'} onClick={()=>setEnv('production')} />
        <Chip label={t('sqlValidation.environments.staging')} color={env==='staging'?'primary':'default'} onClick={()=>setEnv('staging')} />
        <Chip label={t('sqlValidation.environments.beta')} color={env==='beta'?'primary':'default'} onClick={()=>setEnv('beta')} />
        <Chip label={t('sqlValidation.environments.productionNew')} color={env==='production-new'?'primary':'default'} onClick={()=>setEnv('production-new')} />
      </Stack>
      <TextField label={t('sqlValidation.orderIdsLabel')} fullWidth multiline minRows={6} value={ids} onChange={e=>setIds(e.target.value)} />
      <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
        <Button variant="contained" onClick={start} disabled={!ids.trim()}>{t('sqlValidation.start')}</Button>
        <Button variant="outlined" onClick={refresh} disabled={!runId} startIcon={<RefreshIcon />}>{t('sqlValidation.refresh')}</Button>
        <Button variant="text" onClick={download} disabled={!result}>{t('sqlValidation.downloadJson')}</Button>
        <Button variant="text" onClick={downloadCsv} disabled={!result} startIcon={<DownloadIcon />}>{t('sqlValidation.csv')}</Button>
        <FormControlLabel control={<Checkbox checked={autoRefresh} onChange={e=>setAutoRefresh(e.target.checked)} />} label={t('sqlValidation.autoRefresh')} />
        {runId && <Chip label={`${t('sqlValidation.run')}: ${runId}`} />}
      </Stack>
      <Divider sx={{ my: 2 }} />
      {result && (
        <Box>
          <Typography variant="h6">{t('sqlValidation.summary')}</Typography>
          <Stack direction="row" spacing={2} sx={{ my: 1 }}>
            <Chip label={`${t('sqlValidation.status')}: ${result.status}`} color={result.status==='completed'?'success':(result.status==='failed'?'error':'default')} />
            <Chip label={`${t('sqlValidation.passed')}: ${(result.items||[]).filter((i:any)=>i.status==='passed').length}`} color="success" />
            <Chip label={`${t('sqlValidation.failed')}: ${(result.items||[]).filter((i:any)=>i.status==='failed').length}`} color="error" />
            <Chip label={`${t('sqlValidation.error')}: ${(result.items||[]).filter((i:any)=>i.status==='error').length}`} />
            <Chip label={`${t('sqlValidation.total')}: ${(result.items||[]).length}`} />
          </Stack>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField size="small" label={t('sqlValidation.filterByOrderId')} value={filterText} onChange={e=>setFilterText(e.target.value)} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="status-filter">{t('sqlValidation.status')}</InputLabel>
                <Select labelId="status-filter" label={t('sqlValidation.status')} value={filterStatus} onChange={e=>setFilterStatus(e.target.value as any)}>
                  <MenuItem value="all">{t('sqlValidation.filterStatus.all')}</MenuItem>
                  <MenuItem value="pending">{t('sqlValidation.filterStatus.pending')}</MenuItem>
                  <MenuItem value="running">{t('sqlValidation.filterStatus.running')}</MenuItem>
                  <MenuItem value="passed">{t('sqlValidation.filterStatus.passed')}</MenuItem>
                  <MenuItem value="failed">{t('sqlValidation.filterStatus.failed')}</MenuItem>
                  <MenuItem value="error">{t('sqlValidation.filterStatus.error')}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel control={<Checkbox checked={onlyFailed} onChange={e=>setOnlyFailed(e.target.checked)} />} label={t('sqlValidation.onlyFailedError')} />
              <Box flex={1} />
              <IconButton onClick={() => { setFilterText(''); setFilterStatus('all'); setOnlyFailed(false); }} title={t('sqlValidation.clearFilters')}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Paper>

          {/* Table */}
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="50">{t('sqlValidation.table.details')}</TableCell>
                  <TableCell>{t('sqlValidation.table.orderId')}</TableCell>
                  <TableCell>{t('sqlValidation.table.status')}</TableCell>
                  <TableCell>{t('sqlValidation.table.summary')}</TableCell>
                  <TableCell align="right">{t('sqlValidation.table.duration')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i:any) => (
                  <React.Fragment key={i.orderId}>
                    <TableRow hover>
                      <TableCell>
                        {i.details && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newExpanded = new Set(expandedRows);
                              if (newExpanded.has(i.orderId)) {
                                newExpanded.delete(i.orderId);
                              } else {
                                newExpanded.add(i.orderId);
                              }
                              setExpandedRows(newExpanded);
                            }}
                          >
                            {expandedRows.has(i.orderId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{i.orderId}</TableCell>
                      <TableCell>
                        <Chip size="small" label={i.status} color={i.status==='passed'?'success':(i.status==='failed'?'error':(i.status==='running'?'primary':'default'))} />
                      </TableCell>
                      <TableCell>{i.summary}</TableCell>
                      <TableCell align="right">{i.durationMs ?? ''}</TableCell>
                    </TableRow>
                    {i.details && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0 }}>
                          <Collapse in={expandedRows.has(i.orderId)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: i.status === 'passed' ? 'success.50' : 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom color={i.status === 'passed' ? 'success.main' : 'error'}>
                                {i.status === 'passed' ? t('sqlValidation.validationDetails') : t('sqlValidation.errorDetails')}
                              </Typography>
                              <Typography variant="body2" component="pre" sx={{ 
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                bgcolor: 'white',
                                p: 1,
                                border: '1px solid',
                                borderColor: i.status === 'passed' ? 'success.300' : 'grey.300',
                                borderRadius: 1
                              }}>
                                {i.details}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('sqlValidation.noData')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
    </Box>
  );
}


